// v-reveal — marks an element `data-revealed` once it enters the viewport so
// the `reveal` utility can fade/slide it in. Pass the index as the value
// (`v-reveal="i"`) to stagger siblings through the --i custom property.
// One shared IntersectionObserver for the whole app.
import type { Directive } from 'vue';

let observer: IntersectionObserver | null = null;

function observe(el: HTMLElement) {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          (e.target as HTMLElement).dataset.revealed = '';
          observer?.unobserve(e.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 },
    );
  }
  observer.observe(el);
}

export const reveal: Directive<HTMLElement, number | undefined> = {
  mounted(el, binding) {
    if (typeof IntersectionObserver === 'undefined') {
      el.dataset.revealed = '';
      return;
    }
    // Anything already on (or just below) the first screen shows at once;
    // the entrance is for content the visitor scrolls to.
    if (el.getBoundingClientRect().top < window.innerHeight * 1.25) {
      el.dataset.revealed = '';
      return;
    }
    el.classList.add('reveal');
    el.style.setProperty('--i', String(binding.value ?? 0));
    observe(el);
  },
  unmounted(el) {
    observer?.unobserve(el);
  },
};
